import { useEffect, useMemo, useRef, useState } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { motion } from 'framer-motion';
import { Trash2, Download, Grid, List, Calendar, Link, MoreVertical, Edit, Zap, Lock, TrendingUp, Shield, Copy, Pencil, Search, ChevronLeft, ChevronRight, CheckSquare, Square, FileDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useQRCodes, QRCodeWithDynamic } from '@/hooks/useQRCodes';
import { useQRStore } from '@/stores/qrStore';
import { generateQRContent, qrTypeConfig } from '@/lib/qr-utils';
import { formatScanCount, getRedirectUrl } from '@/lib/dynamic-qr-utils';
import { toast } from 'sonner';
import type { FrameSettings, QRDesignSettings } from '@/stores/qrStore';
import { SecurityBadge } from '@/components/security/SecurityWarning';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditDynamicQRModal } from './EditDynamicQRModal';
import { QRSecuritySettings } from './security/QRSecuritySettings';
import { format } from 'date-fns';

function QRCodeThumbnail({ data, designSettings }: { data: string; designSettings: any }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ds = designSettings || {};
    const qrCode = new QRCodeStyling({
      width: 100,
      height: 100,
      data,
      dotsOptions: {
        color: ds.dotsColor || '#1a1a2e',
        type: ds.dotsType || 'rounded',
        ...(ds.gradient?.enabled ? {
          gradient: {
            type: ds.gradient.type || 'linear',
            rotation: (ds.gradient.rotation || 0) * Math.PI / 180,
            colorStops: [
              { offset: 0, color: ds.gradient.color1 || ds.dotsColor || '#1a1a2e' },
              { offset: 1, color: ds.gradient.color2 || ds.dotsColor || '#1a1a2e' },
            ],
          },
        } : {}),
      },
      backgroundOptions: {
        color: ds.backgroundTransparent ? 'transparent' : (ds.backgroundColor || '#ffffff'),
      },
      cornersSquareOptions: {
        type: ds.cornersSquareType || 'extra-rounded',
        color: ds.cornersSquareColor || ds.dotsColor || '#1a1a2e',
      },
      cornersDotOptions: {
        type: ds.cornersDotType || 'dot',
        color: ds.cornersDotColor || ds.dotsColor || '#1a1a2e',
      },
    } as any);

    if (ref.current) {
      ref.current.innerHTML = '';
      qrCode.append(ref.current);
    }
  }, [data, designSettings]);

  return <div ref={ref} className="rounded-lg overflow-hidden" />;
}

export function QRCodeHistory() {
  const { qrCodes, isLoading, deleteQRCode, updateDynamicQR, renameQRCode, dynamicCount } = useQRCodes();
  const { viewMode, setViewMode } = useQRStore();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingQR, setEditingQR] = useState<QRCodeWithDynamic | null>(null);
  const [securityQR, setSecurityQR] = useState<QRCodeWithDynamic | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const PAGE_SIZE = 18;

  const filteredQRCodes = useMemo(() => {
    let result = [...qrCodes];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(qr =>
        qr.name.toLowerCase().includes(q) ||
        qr.type.toLowerCase().includes(q)
      );
    }
    if (typeFilter !== 'all') {
      if (typeFilter === 'dynamic') result = result.filter(qr => qr.isDynamic);
      else if (typeFilter === 'static') result = result.filter(qr => !qr.isDynamic);
      else result = result.filter(qr => qr.type === typeFilter);
    }
    if (statusFilter === 'active') result = result.filter(qr => qr.isActive !== false);
    else if (statusFilter === 'paused') result = result.filter(qr => qr.isDynamic && qr.isActive === false);
    return result;
  }, [qrCodes, searchQuery, typeFilter, statusFilter]);

  // Reset to page 1 and clear selection when filters change
  useEffect(() => { setCurrentPage(1); setSelectedIds(new Set()); }, [searchQuery, typeFilter, statusFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedQRCodes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedQRCodes.map(q => q.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const dynamicCount = qrCodes.filter(q => selectedIds.has(q.id) && q.isDynamic).length;
    const msg = `Delete ${selectedIds.size} QR code(s)?` + (dynamicCount > 0 ? ` (${dynamicCount} dynamic — scan data will be lost)` : '');
    if (!window.confirm(msg)) return;
    let deleted = 0;
    for (const id of selectedIds) {
      try { await deleteQRCode.mutateAsync(id); deleted++; } catch {}
    }
    toast.success(`Deleted ${deleted} QR code(s).`);
    setSelectedIds(new Set());
  };

  const handleBulkExportCSV = () => {
    const selected = qrCodes.filter(q => selectedIds.has(q.id));
    if (selected.length === 0) return;
    const rows = [
      ['Name', 'Type', 'Dynamic', 'Active', 'Scans', 'Created', 'Content'].join(','),
      ...selected.map(q => [
        `"${q.name.replace(/"/g, '""')}"`,
        q.type,
        q.isDynamic ? 'Yes' : 'No',
        q.isActive !== false ? 'Yes' : 'No',
        q.isDynamic ? q.clicksCount || 0 : '',
        format(new Date(q.createdAt), 'yyyy-MM-dd'),
        `"${(q.isDynamic && q.redirectUrl ? q.redirectUrl : generateQRContent(q.type, q.contentData)).replace(/"/g, '""')}"`,
      ].join(',')),
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nexqr-export-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selected.length} QR code(s).`);
  };

  const totalPages = Math.max(1, Math.ceil(filteredQRCodes.length / PAGE_SIZE));
  const paginatedQRCodes = filteredQRCodes.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleDownload = async (qr: QRCodeWithDynamic) => {
    const content = qr.isDynamic && qr.redirectUrl
      ? qr.redirectUrl
      : generateQRContent(qr.type, qr.contentData);

    const ds = qr.designSettings || {} as Partial<QRDesignSettings>;
    const res = ds.resolution || 1000;
    const hasValidLogo = ds.logo?.enabled && ds.logo?.url;

    const qrCode = new QRCodeStyling({
      width: res,
      height: res,
      data: content,
      dotsOptions: {
        color: ds.dotsColor || '#1a1a2e',
        type: (ds.dotsType || 'rounded') as any,
        ...(ds.gradient?.enabled ? {
          gradient: {
            type: ds.gradient.type || 'linear',
            rotation: (ds.gradient.rotation || 0) * Math.PI / 180,
            colorStops: [
              { offset: 0, color: ds.gradient.color1 || ds.dotsColor || '#1a1a2e' },
              { offset: 1, color: ds.gradient.color2 || ds.dotsColor || '#1a1a2e' },
            ],
          },
        } : {}),
      },
      backgroundOptions: {
        color: ds.backgroundTransparent ? 'transparent' : (ds.backgroundColor || '#ffffff'),
      },
      cornersSquareOptions: {
        type: (ds.cornersSquareType || 'extra-rounded') as any,
        color: ds.cornersSquareColor || ds.dotsColor || '#1a1a2e',
      },
      cornersDotOptions: {
        type: (ds.cornersDotType || 'dot') as any,
        color: ds.cornersDotColor || ds.dotsColor || '#1a1a2e',
      },
      image: hasValidLogo ? ds.logo!.url : undefined,
      imageOptions: {
        crossOrigin: 'anonymous' as const,
        margin: hasValidLogo ? 10 : 0,
        imageSize: hasValidLogo ? (ds.logo!.size || 25) / 100 : 0,
        hideBackgroundDots: hasValidLogo ? ds.logo!.transparentBackground : false,
      },
      qrOptions: {
        errorCorrectionLevel: hasValidLogo ? 'H' : (ds.errorCorrectionLevel || 'M'),
      },
      margin: ds.margin ?? 10,
    } as any);

    const frame = ds.frame;
    const hasFrame = frame && frame.style !== 'none';

    if (!hasFrame) {
      qrCode.download({ name: qr.name, extension: (ds.downloadFormat || 'png') as any });
    } else {
      try {
        const qrRaw = await qrCode.getRawData('png');
        if (!qrRaw) throw new Error('Failed to generate QR image');
        const qrImg = await createImageBitmap(qrRaw as Blob);

        // Inline minimal frame compositing (mirrors drawFrameOnCanvas logic)
        const scale = res / 300;
        const borderW = Math.round(4 * scale);
        const textH = Math.round(28 * scale);
        const hasText = ['scan-me-top','scan-me-bottom','tag','badge','clipboard','laptop','watch','camera','bag','speech-bubble','banner'].includes(frame.style);
        const pad = Math.round(20 * scale);
        const topExtra = hasText ? textH + pad : pad;
        const bottomExtra = hasText ? textH + pad : pad;
        const cw = res + pad * 2;
        const ch = res + topExtra + bottomExtra;
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = ds.backgroundColor || '#ffffff';
        ctx.fillRect(0, 0, cw, ch);
        ctx.strokeStyle = frame.color || '#1a1a2e';
        ctx.lineWidth = borderW;
        ctx.strokeRect(borderW / 2, borderW / 2, cw - borderW, ch - borderW);
        ctx.drawImage(qrImg, pad, topExtra, res, res);
        if (hasText && frame.text) {
          ctx.fillStyle = frame.color || '#1a1a2e';
          ctx.font = `bold ${Math.round(14 * scale)}px sans-serif`;
          ctx.textAlign = 'center';
          const textY = hasText ? topExtra - Math.round(8 * scale) : ch - Math.round(8 * scale);
          ctx.fillText(frame.text, cw / 2, textY);
        }
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('Export failed')), 'image/png', 0.95);
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${qr.name}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        // Fallback: download without frame
        qrCode.download({ name: qr.name, extension: 'png' });
      }
    }
  };

  const handleCopyLink = (qr: QRCodeWithDynamic) => {
    if (qr.shortSlug) {
      navigator.clipboard.writeText(getRedirectUrl(qr.shortSlug));
      toast.success('Redirect link copied!');
    } else if (qr.redirectUrl) {
      navigator.clipboard.writeText(qr.redirectUrl);
      toast.success('Redirect link copied!');
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteQRCode.mutateAsync(deleteId);
      setDeleteId(null);
    }
  };

  const handleRenameStart = (qr: QRCodeWithDynamic) => {
    setRenamingId(qr.id);
    setRenameValue(qr.name);
  };

  const handleRenameSubmit = async () => {
    if (renamingId && renameValue.trim()) {
      await renameQRCode.mutateAsync({ id: renamingId, name: renameValue.trim() });
      setRenamingId(null);
      setRenameValue('');
    }
  };

  const handleEditSave = async (id: string, updates: { 
    contentData: Record<string, string>; 
    isActive: boolean; 
    analyticsConfig?: any; 
    blockchainConfig?: any;
    routingRules?: any[];
  }) => {
    await updateDynamicQR.mutateAsync({ id, ...updates });
  };

  // Get QR content for thumbnail
  const getQRContent = (qr: QRCodeWithDynamic) => {
    if (qr.isDynamic && qr.redirectUrl) {
      return qr.redirectUrl;
    }
    return generateQRContent(qr.type, qr.contentData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (qrCodes.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-12"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
          <Link className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">No QR Codes Yet</h3>
        <p className="text-muted-foreground">Create your first QR code to see it here</p>
      </motion.div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">My QR Codes</h2>
          <Badge variant="outline" className="text-xs">
            {dynamicCount} dynamic
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={handleBulkExportCSV}>
            <FileDown className="h-4 w-4 mr-1" /> Export CSV
          </Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search QR codes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-10 px-3 gap-2 shrink-0"
          onClick={toggleSelectAll}
          title={selectedIds.size === paginatedQRCodes.length ? 'Deselect all' : 'Select all on page'}
        >
          {selectedIds.size === paginatedQRCodes.length && paginatedQRCodes.length > 0 ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
          <span className="hidden sm:inline text-xs">Select All</span>
        </Button>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-10 px-3 rounded-md border bg-background text-sm"
        >
          <option value="all">All Types</option>
          <option value="dynamic">Dynamic Only</option>
          <option value="static">Static Only</option>
          {Object.entries(qrTypeConfig).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 rounded-md border bg-background text-sm"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {filteredQRCodes.length === 0 && qrCodes.length > 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No QR codes match your filters</p>
        </div>
      )}

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedQRCodes.map((qr, index) => (
            <motion.div
              key={qr.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="surface-interactive rounded-xl p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Checkbox
                    checked={selectedIds.has(qr.id)}
                    onCheckedChange={() => toggleSelect(qr.id)}
                    className="shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {renamingId === qr.id ? (
                      <form
                        className="flex items-center gap-1"
                        onSubmit={(e) => { e.preventDefault(); handleRenameSubmit(); }}
                      >
                        <input
                          autoFocus
                          className="h-6 px-1.5 text-sm font-medium border rounded bg-background w-28"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onBlur={handleRenameSubmit}
                          onKeyDown={(e) => { if (e.key === 'Escape') { setRenamingId(null); } }}
                        />
                      </form>
                    ) : (
                      <h3 className="font-medium truncate">{qr.name}</h3>
                    )}
                    {qr.isDynamic ? (
                      <Badge className="text-[10px] px-1.5 py-0 bg-secondary text-secondary-foreground">
                        <Zap className="h-2.5 w-2.5 mr-0.5" />
                        Dynamic
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        <Lock className="h-2.5 w-2.5 mr-0.5" />
                        Static
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      {qrTypeConfig[qr.type]?.label || qr.type}
                    </p>
                    {(qr as any).security_status && (
                      <SecurityBadge status={(qr as any).security_status} />
                    )}
                  </div>
                </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {qr.isDynamic && (
                      <>
                        <DropdownMenuItem onClick={() => setEditingQR(qr)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Destination
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSecurityQR(qr)}>
                          <Shield className="h-4 w-4 mr-2" />
                          Security Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopyLink(qr)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Link
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => handleRenameStart(qr)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownload(qr)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => setDeleteId(qr.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex justify-center py-3 bg-muted/50 rounded-lg relative">
                <QRCodeThumbnail
                  data={getQRContent(qr)}
                  designSettings={qr.designSettings}
                />
                {qr.isDynamic && !qr.isActive && (
                  <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg">
                    <Badge variant="outline" className="text-xs">Paused</Badge>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(qr.createdAt), 'MMM d, yyyy')}
                </div>
                {qr.isDynamic && (
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {formatScanCount(qr.clicksCount)} scans
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {paginatedQRCodes.map((qr, index) => (
            <motion.div
              key={qr.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03 }}
              className="surface-interactive rounded-xl p-4 flex items-center gap-4"
            >
              <Checkbox
                checked={selectedIds.has(qr.id)}
                onCheckedChange={() => toggleSelect(qr.id)}
                className="shrink-0"
              />
              <div className="flex-shrink-0 relative">
                <QRCodeThumbnail
                  data={getQRContent(qr)}
                  designSettings={qr.designSettings}
                />
                {qr.isDynamic && !qr.isActive && (
                  <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg">
                    <Badge variant="outline" className="text-[10px]">Paused</Badge>
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {renamingId === qr.id ? (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={(e) => { e.preventDefault(); handleRenameSubmit(); }}
                    >
                      <input
                        autoFocus
                        className="h-6 px-1.5 text-sm font-medium border rounded bg-background w-36"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={handleRenameSubmit}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setRenamingId(null); } }}
                      />
                    </form>
                  ) : (
                    <h3 className="font-medium truncate">{qr.name}</h3>
                  )}
                  {qr.isDynamic ? (
                    <Badge className="text-[10px] px-1.5 py-0 bg-secondary text-secondary-foreground">
                      <Zap className="h-2.5 w-2.5 mr-0.5" />
                      Dynamic
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      <Lock className="h-2.5 w-2.5 mr-0.5" />
                      Static
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {qrTypeConfig[qr.type]?.label || qr.type}
                </p>
                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(qr.createdAt), 'MMM d, yyyy')}
                  </div>
                  {qr.isDynamic && (
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {formatScanCount(qr.clicksCount)} scans
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {qr.isDynamic && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setEditingQR(qr)}
                      title="Edit Destination"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setSecurityQR(qr)}
                      title="Security Settings"
                    >
                      <Shield className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopyLink(qr)}
                      title="Copy Link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRenameStart(qr)}
                  title="Rename"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(qr)}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteId(qr.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredQRCodes.length)} of {filteredQRCodes.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium px-2">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete QR Code</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this QR code? This action cannot be undone.
              {qrCodes.find(q => q.id === deleteId)?.isDynamic && (
                <span className="block mt-2 font-medium text-destructive">
                  Warning: This is a dynamic QR code. All scan tracking data will be lost.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditDynamicQRModal
        qr={editingQR}
        open={!!editingQR}
        onOpenChange={(open) => !open && setEditingQR(null)}
        onSave={handleEditSave}
      />

      {securityQR && (
        <QRSecuritySettings
          qrCode={{
            id: securityQR.id,
            name: securityQR.name,
            passwordEnabled: (securityQR as any).passwordEnabled,
            expiresAt: (securityQR as any).expiresAt,
            scanLimit: (securityQR as any).scanLimit,
            uniqueScanLimit: (securityQR as any).uniqueScanLimit,
            geoWhitelist: (securityQR as any).geoWhitelist,
            geoBlacklist: (securityQR as any).geoBlacklist,
            blockedRedirectUrl: (securityQR as any).blockedRedirectUrl,
            captchaEnabled: (securityQR as any).captchaEnabled,
            botProtectionEnabled: (securityQR as any).botProtectionEnabled,
          }}
          open={!!securityQR}
          onOpenChange={(open) => !open && setSecurityQR(null)}
          onSave={() => {}}
        />
      )}
    </>
  );
}
